import { useQuery } from '@tanstack/react-query';
import { getRequest } from '../../../../../services/httpService';

// ---------------------------------------------------------------------
// API Response Types - Updated for item-store-clinic-search API
// ---------------------------------------------------------------------

export interface PharmacyItemBatch {
  id: number;
  batch_number: string;
  expiry_date: string;
  base_cost: number;
  base_cost_price: number;
  base_mrp: number;
  available_stock: number;
  available_uom_id: number;
  available_uom_name: string;
  available_uom_static_field: string;
  conversion_factor: number;
  sgst_percent?: number;
  sgst_amount?: number;
  cgst_percent?: number;
  cgst_amount?: number;
  igst_percent?: number;
  igst_amount?: number;
  store_id?: number;
  is_hold?: boolean;
}

export interface PharmacyItemUOMConversion {
  id: number | null;
  item_id: number;
  item_name: string;
  uom_id: number;
  uom_code: string;
  uom_static_field: string;
  code: string;
  conversion_factor: number;
  converted_mrp: number;
  converted_cost_price: number;
  uom?: string;
}

export interface PharmacyItem {
  id: number;
  batches: PharmacyItemBatch[];
  available_qty: number;
  total_qty: number;
  total_amount: number;
  item_id: number;
  item_name: string;
  item_code: string;
  hsn_code_id: number | null;
  hsn_code: string | null;
  clinic_id: number;
  clinic_name: string;
  dept_id: number;
  dept_name: string;
  shelf_id: number;
  shelf_name: string;
  base_uom_id: number;
  base_uom_name: string;
  base_uom_static_field: string;
  purchase_uom_id: number;
  purchase_uom_name: string;
  rack_id: number;
  rack_name: string;
  bin_id: number;
  bin_name: string;
  min_qty: number;
  max_qty: number;
  reorder_level: string;
  is_active: boolean;
  uom_conversions: PharmacyItemUOMConversion[];
  item_group_id: string;
  item_category_id: string;
  mrp: string;
  cost_price: string;
  min_expiry_in_days: number;
  is_perishable: boolean;
  is_free: boolean;
  item_category_name: string;
  item_group_name: string;
  CGST: number;
  SGST: number;
  IGST: number;
  pregnancy_class_id?: number | null;
  pregnancy_class_name?: string | null;
  package_details?: Array<{
    package_id: number;
    package_name: string;
    package_pharmacy_item_id: number | null;
    item_id: number;
    item_name: string;
    item_code: string;
    total_quantity: number | null;
    quantity_consumed: number | null;
    quantity_remaining: number | null;
    uom_id: number | null;
    uom_code: string | null;
    uom_description: string | null;
    discount_percent: number;
    discount_amount_per_unit: number;
    total_discount_amount: number;
    unit_price: number;
    mrp: number;
    discounted_unit_price: number;
    pharmacy_amount: number;
    pharmacy_remaining_amount: number;
    is_set_all_items?: boolean;
  }>;
}

export interface ItemSearchResponse {
  meta: {
    limit: number;
    next: string | null;
    offset: number;
    previous: string | null;
    total_count: number;
  };
  objects: PharmacyItem[];
}

// ---------------------------------------------------------------------
// API Query Function - Updated to not require dept_id
// ---------------------------------------------------------------------

export function fetchItemSearch(
  searchTerm: string,
  clinicId: number,
  deptId: number,
  packageId?: number | null,
  tariffId?: number | null,
  patientId?: number | null
): Promise<ItemSearchResponse> {
  const encodedTerm = encodeURIComponent(searchTerm.trim());
  let url = `item-store-clinic-search/?clinic_id=${clinicId}&dept_id=${deptId}&search=${encodedTerm}&limit=20`;
  
  // Only add package_id if it's provided (when editing from package item)
  if (packageId) {
    url += `&package_id=${packageId}`;
  }
  
  // Add tariff_id if provided
  if (tariffId) {
    url += `&tariff_id=${tariffId}`;
  }

  // Add patient_id if provided
  if (patientId) {
    url += `&patient_id=${patientId}`;
  }
  
  return getRequest(url);
}

// ---------------------------------------------------------------------
// React Query Hook - Updated to not require dept_id
// ---------------------------------------------------------------------

export function useItemSearch(
  searchTerm: string,
  clinicId: number | undefined,
  deptId: number | undefined | null,
  enabled: boolean,
  packageId?: number | null,
  tariffId?: number | null,
  patientId?: number | null
) {
  const trimmedTerm = searchTerm.trim();
  
  return useQuery<ItemSearchResponse>({
    queryKey: ['pharmacy', 'item-store-search', trimmedTerm, clinicId, deptId, packageId, tariffId, patientId],
    queryFn: () => fetchItemSearch(trimmedTerm, clinicId!, deptId!, packageId, tariffId, patientId),
    enabled: enabled && trimmedTerm.length > 0 && !!clinicId && !!deptId,
    staleTime: 60_000,
    gcTime: 300_000,
    retry: 1,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    placeholderData: (previousData) => previousData,
    refetchOnReconnect: false,
  });
}
